'use client'
import * as React from 'react'
import type { Clubhouse } from 'codegen/types'
import { useTranslations } from 'next-intl'
import { Link } from '@repo/web-ui/components/link'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { motion } from 'framer-motion'
import { Select } from '@repo/web-ui/components/select/select'
import { SearchIcon } from 'primitives/icons'
import { Text } from 'primitives/form-controls/text'
import { AppRoutesParameterized, AppRoutes } from '@repo/web-utils/app-routes'
import { useIsLoggedInQuery } from 'auth/hooks'
import Image from 'next/image'
import { SkeletonItem } from 'components/skeleton-item'
import { tw } from 'utils/classnames'
import { formatString } from '../_utils/format-string'

import { LFS_URL } from '@web/utils/env'
import { useClubhousesFromUserQuery } from '@web/gen/crafter'
import { Chip } from '../[slug]/_components/chip'
import { useClubhousesListAllQuery } from '@web/gen/clubhouses'
import type { ClubhouseType } from '@web/gen/types'
import { ClubhousePopularCard } from './clubhouse-popular-card'
import { ClubhouseCard } from './clubhouse-card'
import { NoSearchResults } from './no-search-results'

const sortOptions = [
  {
    value: 'asc',
    label: 'A-Z',
  },
  {
    value: 'desc',
    label: 'Z-A',
  },
  {
    value: 'popularity',
    label: 'Sort by popularity',
  },
]

interface ClubhouseContentProps {
  clubhouses: Clubhouse[]
  clubhousesTypes?: string[]
}

export function ClubhouseContent({ clubhouses, clubhousesTypes }: ClubhouseContentProps) {
  const t = useTranslations('pages.clubhouse')
  const user = useUser()
  const { data: isLoggedIn } = useIsLoggedInQuery()

  const [filter, setFilter] = React.useState('all')
  const [filterText, setFilterText] = React.useState('')
  const [sortMethod, setSortMethod] = React.useState(sortOptions[0].value)
  // https://creativefabrica.atlassian.net/browse/SOCIAL-1635 - Getting rid of the first item being trimmed
  // on hover due to lack of space for scaling effect
  const [isItemHovered, setIsItemHovered] = React.useState(false)
  const [isFirstActive, setIsFirstActive] = React.useState(true)
  const [isLastActive, setIsLastActive] = React.useState(false)
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const chipScrollRef = React.useRef<HTMLDivElement>(null)
  const chipRefs = React.useRef<Record<string, HTMLButtonElement | null>>({})
  const clubhouseTypeList = React.useMemo(() => {
    if (!clubhousesTypes || clubhousesTypes.length === 0) {
      return [{ name: 'All', value: 'all' }]
    }

    return [
      { name: 'All', value: 'all' },
      ...clubhousesTypes.map(type => ({
        name: formatString(type),
        value: type,
      })),
    ]
  }, [clubhousesTypes])

  // Initialize from URL on client-side only (no Suspense needed since we don't use useSearchParams)
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      const searchParam = urlParams.get('search') || ''
      const queryParam = urlParams.get('query') || ''

      const validTypes = clubhouseTypeList.map(t => t.value)
      if (searchParam && validTypes.includes(searchParam)) {
        setFilter(searchParam)
      }
      if (queryParam) {
        setFilterText(queryParam)
      }
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  const { data: filteredClubhouses } = useClubhousesListAllQuery(
    {
      page: 1,
      itemsPerPage: 20,
      // oxlint-disable-next-line no-negated-condition
      filter: filter !== 'all' ? (filter as ClubhouseType) : undefined,
    },
    { enabled: filter !== 'all' },
  )
  const { data: yourClubhouses } = useClubhousesFromUserQuery(
    {
      input: { userId: user.data?.id ?? '', page: 0, itemsPerPage: 15 },
    },
    {
      enabled: Boolean(user.data?.id),
      select: data => data?.clubhousesFromUser?.clubhouses ?? [],
    },
  )

  const mostPopular = [...clubhouses]
    .toSorted((a, b) => b.membersTotal - a.membersTotal)
    .slice(0, 3)

  const dataToUse = React.useMemo(() => {
    // oxlint-disable-next-line no-negated-condition
    return filter !== 'all' ? filteredClubhouses?.clubhousesListAll.clubhouses || [] : clubhouses
  }, [filter, filteredClubhouses, clubhouses])

  // Memoized callback to sort the array
  const sortedArray = React.useCallback(() => {
    // Create a filtered array based on the search text
    const filtered =
      dataToUse?.filter(clubhouse => {
        const matchesText =
          !filterText ||
          clubhouse.name.toLowerCase().includes(filterText.toLowerCase()) ||
          clubhouse.type.toLowerCase().includes(filterText.toLowerCase())

        const matchesType = filter === 'all' || clubhouse.type === filter

        return matchesText && matchesType
      }) || []

    // Then sort the filtered array
    const sorted = [...filtered]
    switch (sortMethod) {
      case 'asc': {
        return sorted.toSorted((a, b) => a.name.localeCompare(b.name))
      }
      case 'desc': {
        return sorted.toSorted((a, b) => b.name.localeCompare(a.name))
      }
      case 'popularity': {
        return sorted.toSorted((a, b) => b.membersTotal - a.membersTotal)
      }
      default: {
        return sorted
      }
    }
  }, [dataToUse, filterText, sortMethod, filter])

  const handleFilterText = (text: string) => {
    setFilterText(text)

    const currentParams = new URLSearchParams(window.location.search)

    if (text) {
      currentParams.set('search', text)
    } else {
      currentParams.delete('search')
    }
    const newSearch = currentParams.get('search') || ''
    const newUrl = AppRoutesParameterized.CLUBHOUSE_SEARCH_RESULTS(newSearch)

    window.history.replaceState({ ...window.history.state, as: newUrl, url: newUrl }, '', newUrl)
  }

  const updateSearchParam = (newFilter: string) => {
    const currentParams = new URLSearchParams(window.location.search)

    if (newFilter === 'all') {
      currentParams.delete('search')
    } else {
      currentParams.set('search', newFilter)
    }

    const newSearch = currentParams.get('search') || ''
    const newUrl = AppRoutesParameterized.CLUBHOUSE_SEARCH_RESULTS(newSearch)

    window.history.replaceState(null, '', newUrl)
  }

  const handleChipFilterClick = (value: string) => {
    const newFilter = value === 'all' ? 'all' : value
    setFilter(newFilter)
    updateSearchParam(newFilter)
  }

  const handleItemMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsItemHovered(true)
  }

  const handleItemMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsItemHovered(false)
    }, 300)
  }

  const currentPillFilterText = clubhouseTypeList.find(type => type.value === filter)?.name

  React.useEffect(() => {
    const container = chipScrollRef.current
    const selectedChip = chipRefs.current[filter]
    const firstChip = chipRefs.current[clubhouseTypeList[0].value]
    // oxlint-disable-next-line unicorn/prefer-at
    const lastChip = chipRefs.current[clubhouseTypeList[clubhouseTypeList.length - 1]?.value]

    if (!container || !selectedChip || !firstChip || !lastChip) {
      return
    }

    const isFirst = filter === clubhouseTypeList[0].value
    // oxlint-disable-next-line unicorn/prefer-at
    const isLast = filter === clubhouseTypeList[clubhouseTypeList.length - 1]?.value

    const chipOffsetLeft = selectedChip.offsetLeft
    const chipWidth = selectedChip.offsetWidth
    const containerWidth = container.offsetWidth

    let scrollTo = 0

    if (isFirst) {
      scrollTo = chipOffsetLeft - 8
    } else if (isLast) {
      scrollTo = chipOffsetLeft - (containerWidth - chipWidth) + 8
    } else {
      scrollTo = chipOffsetLeft - containerWidth / 2 + chipWidth / 2
    }

    container.scrollTo({
      left: scrollTo,
      behavior: 'smooth',
    })

    // delay visibility check until after scroll settles
    const checkAfterScroll = () => {
      const containerScrollLeft = container.scrollLeft
      const containerScrollRight = containerScrollLeft + container.clientWidth

      const firstChipLeft = firstChip.offsetLeft
      const lastChipRight = lastChip.offsetLeft + lastChip.offsetWidth

      const isFirstFullyVisible = containerScrollLeft <= firstChipLeft
      const isLastFullyVisible = lastChipRight <= containerScrollRight + 1

      setIsFirstActive(isFirst && isFirstFullyVisible)
      setIsLastActive(isLast && isLastFullyVisible)
    }

    const scrollCheckTimeout = setTimeout(checkAfterScroll, 300)

    return () => clearTimeout(scrollCheckTimeout)
  }, [filter, clubhouseTypeList])

  return (
    <div className="mx-auto max-w-screen-xl px-4 py-6 lg:px-8 lg:pb-20 lg:pt-10">
      <div
        className="relative mb-8 rounded-[12px] bg-[#2A1F3F] p-6 md:p-[48px] xl:p-[64px]"
        style={{
          background:
            'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.00) 100%), #422D75',
        }}
      >
        <div className="flex justify-between">
          <div className="lg:max-w-[calc(100%-540px)] xl:max-w-[600px]">
            <Image
              src={`${LFS_URL}/web/pages/clubhouses/clubhouse-logo.png`}
              alt="Clubhouse Logo"
              width={220}
              height={56}
              className="mb-4 h-11 w-[173px] lg:h-[56px] lg:w-[220px]"
            />
            <p className="text-cf-sf-gray-75 text-14 whitespace-pre-wrap leading-6 lg:text-[16px] hidden tablet:block">
              {t('clubhouse_homepage.description')}
            </p>
            <p className="text-cf-sf-gray-75 text-14 whitespace-pre-wrap leading-6 tablet:hidden block">
              {t('clubhouse_homepage.description_short')}
            </p>
          </div>
          <Image
            src={`${LFS_URL}/web/pages/clubhouses/home-page-banner.png`}
            alt="Clubhouse Header"
            width={580}
            height={346}
            className="pointer-events-none absolute -right-6 -top-24 hidden lg:block"
          />
        </div>
      </div>
      {isLoggedIn && yourClubhouses?.length ? (
        <div className="mb-10">
          <h2 className="mb-4 w-full text-[28px] font-bold leading-8 md:text-[32px] md:leading-10">
            {t('your_clubhouses')}
          </h2>

          <div className="relative">
            <div
              className="pointer-events-none absolute inset-0 z-[20]"
              style={{
                background: `linear-gradient(
                    90deg,
                    #f4f5f7 0%,
                    rgba(244, 245, 247, 0.00) 8%,
                    rgba(244, 245, 247, 0.00) 92%,
                    #f4f5f7 100%
                  )`,
              }}
            />
            <div
              className={tw(
                'scroller no-scrollbar relative z-20 -ml-1 flex max-w-full flex-row items-center gap-8 overflow-x-auto whitespace-nowrap px-1 py-1',
                isItemHovered ? '-ml-1 pl-1' : '',
              )}
            >
              {yourClubhouses.map(clubhouse => {
                return (
                  <div key={clubhouse.slug}>
                    <motion.div
                      whileHover="hover"
                      className="group flex max-w-24 flex-col items-center"
                      onMouseEnter={handleItemMouseEnter}
                      onMouseLeave={handleItemMouseLeave}
                    >
                      <Link
                        href={`${AppRoutes.CLUBHOUSE}${clubhouse.slug}`}
                        className="flex flex-col items-center"
                      >
                        <motion.div
                          variants={{
                            hover: { scale: 1.03 },
                            initial: { scale: 1 },
                          }}
                          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                          className={tw(
                            'flex h-[72px] w-[72px] items-center justify-center rounded-full border border-[#0A0A0B1A] group-hover:opacity-60 group-active:opacity-80',
                            `bg-[${clubhouse?.bgColor}]`,
                          )}
                        >
                          <Image
                            src={clubhouse?.avatarImage || ''}
                            alt="logo"
                            width={66}
                            height={66}
                          />
                        </motion.div>
                        <p className="text-12 pt-2 text-center font-semibold group-hover:underline">
                          {clubhouse.name}
                        </p>
                      </Link>
                    </motion.div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      ) : null}

      <div className="mb-10">
        <h2 className="mb-4 w-full text-[28px] font-bold leading-8 md:text-[32px] md:leading-10">
          {t(`popular_clubhouses`)}
        </h2>
        <div className="tablet:flex-row flex flex-col gap-4 pb-2">
          {mostPopular.map((clubhouse, index) => {
            return <ClubhousePopularCard clubhouse={clubhouse} key={clubhouse.slug} index={index} />
          })}
        </div>
      </div>
      <>
        <div className="flex flex-col md:flex-row">
          <h2 className="mb-6 w-full text-[28px] font-bold leading-8 md:text-[32px] md:leading-10 lg:mb-4">
            {t(`all_clubhouses`)}
          </h2>
          <div className="tablet:min-w-[264px] bg-cf-white rounded-2 mb-4 flex h-10 items-center justify-between gap-[7px] border border-[#e0e2ea] bg-white px-3 lg:mb-0">
            <div className="flex h-8 flex-1 items-center overflow-hidden">
              <Text
                id="search-string"
                value={filterText || ''}
                className="placeholder-text-context text-text-primary w-full bg-white text-sm placeholder-opacity-50 outline-none ring-0"
                type="text"
                placeholder="Search Clubhouses"
                onChange={e => handleFilterText(e.target.value)}
              />
            </div>
            <SearchIcon className="text-cf-sf-gray-1150 size-4" />
          </div>
        </div>
        <div
          className={tw(isFirstActive ? '-mr-4' : '-mx-4', 'flex flex-wrap items-center sm:-mx-0')}
        >
          <div className="relative mb-4 w-full">
            <div
              className="pointer-events-none absolute inset-0 z-50 sm:hidden"
              style={{
                background: `
                  linear-gradient(
                    to right,
                    ${isFirstActive ? 'transparent' : '#f5f6fa'} 0%,
                    rgba(245,246,250,0) 8%,
                    rgba(245,246,250,0) 92%,
                    ${isLastActive ? 'transparent' : '#f5f6fa'} 100%
                  )
                `,
              }}
            />
            <div
              ref={chipScrollRef}
              className="scroller no-scrollbar relative z-20 -ml-1 flex max-w-full flex-row items-center gap-2 overflow-x-auto scroll-smooth whitespace-nowrap p-1"
            >
              {clubhouseTypeList.map(type => (
                <Chip
                  key={type.value}
                  text={type.name}
                  onClick={() => handleChipFilterClick(type.value)}
                  enabled={filter === type.value}
                  ref={(el: HTMLButtonElement | null) => {
                    chipRefs.current[type.value] = el
                  }}
                />
              ))}
            </div>
          </div>

          <div className="my-2 ml-auto mr-4 flex-shrink-0 sm:mr-0">
            <Select
              classNameTrigger="min-w-20 bg-transparent border-0 text-cf-sf-gray-1050 font-bold focus-visible:outline-none"
              value={sortMethod}
              onValueChange={e => {
                setSortMethod(e)
              }}
              items={sortOptions}
              position="popper"
            />
          </div>
        </div>

        {sortedArray().length > 0 ? (
          <div className="grid w-full grid-cols-1 items-center justify-center gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sortedArray().map((clubhouse, index) => {
              return <ClubhouseCard clubhouse={clubhouse as Clubhouse} key={index} />
            })}
          </div>
        ) : (
          <NoSearchResults
            // oxlint-disable-next-line no-negated-condition
            currentFilter={filter !== 'all' ? currentPillFilterText : undefined}
            resetFilters={() => {
              setFilter('all')
              updateSearchParam('all')
            }}
          />
        )}
      </>
    </div>
  )
}

export function ClubhouseSkeleton() {
  return (
    <div className="mx-auto max-w-screen-xl px-4 py-6 lg:px-8 lg:pb-20 lg:pt-10">
      <SkeletonItem className="mb-8 h-[284px] w-full rounded-[12px] lg:h-[302px]" />
      <div className="mb-10">
        <SkeletonItem className="h-8 w-80 lg:h-10" />
        <div className="mt-4 flex flex-col gap-4 md:flex-row">
          {Array.from({ length: 3 }, (_, index) => (
            <SkeletonItem key={index} className="h-[333px] w-full rounded-[12px] md:w-1/3" />
          ))}
        </div>
      </div>
      <div className="mb-6 flex flex-col justify-between md:flex-row">
        <SkeletonItem className="h-8 w-40 lg:h-10" />
        <SkeletonItem className="h-10 w-40" />
      </div>
      <div className="grid w-full grid-cols-1 items-center justify-center gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }, (_, index) => (
          <SkeletonItem key={index} className="h-[134px] w-full rounded-[12px] lg:h-[150px]" />
        ))}
      </div>
    </div>
  )
}
